import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Lock, CheckCircle, AlertCircle, Eye, EyeOff, Building2 } from 'lucide-react';
import authService from '../services/authService';

function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();

    // Token validation state
    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenError, setTokenError] = useState('');
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');

    // Form state
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    // Validate token on mount
    useEffect(() => {
        if (!token) {
            setTokenError('No reset token provided.');
            setValidating(false);
            return;
        }

        const validateToken = async () => {
            try {
                const response = await authService.validateResetToken(token);
                if (response.success && response.data.valid) {
                    setTokenValid(true);
                    setUserName(response.data.name || 'User');
                    setUserEmail(response.data.email || '');
                } else {
                    setTokenError('This reset link is invalid or has expired.');
                }
            } catch (err) {
                setTokenError(err?.error || 'This reset link is invalid or has expired.');
            } finally {
                setValidating(false);
            }
        };

        validateToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        try {
            const response = await authService.resetPassword(token, password, confirmPassword);
            setSuccess(true);
        } catch (err) {
            setError(err?.error || err?.message || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (validating) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f0c1f 0%, #1a1533 50%, #0f0c1f 100%)' }}>
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 mx-auto"
                        style={{ background: 'rgba(155,125,240,0.15)' }}>
                        <Lock className="h-8 w-8" style={{ color: '#cbb9ff' }} />
                    </div>
                    <p className="text-white text-lg">Validating your reset link...</p>
                    <div className="mt-4 flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#cbb9ff' }}></div>
                    </div>
                </div>
            </div>
        );
    }

    // Token invalid
    if (!tokenValid) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f0c1f 0%, #1a1533 50%, #0f0c1f 100%)' }}>
                <div className="glass-card rounded-2xl w-full max-w-md p-8 mx-4" style={{ background: 'rgba(14,14,26,0.95)' }}>
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 mx-auto"
                            style={{ background: 'rgba(239,68,68,0.15)' }}>
                            <AlertCircle className="h-8 w-8" style={{ color: '#fca5a5' }} />
                        </div>
                        <h2 className="text-xl font-bold text-white mb-2">Invalid or Expired Link</h2>
                        <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.5)' }}>
                            {tokenError || 'This password reset link is invalid or has expired.'}
                        </p>
                        <Link
                            to="/login"
                            className="btn-primary inline-block w-full text-center"
                        >
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Success
    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f0c1f 0%, #1a1533 50%, #0f0c1f 100%)' }}>
                <div className="glass-card rounded-2xl w-full max-w-md p-8 mx-4" style={{ background: 'rgba(14,14,26,0.95)' }}>
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 mx-auto"
                            style={{ background: 'rgba(74,222,128,0.15)' }}>
                            <CheckCircle className="h-8 w-8" style={{ color: '#4ade80' }} />
                        </div>
                        <h2 className="text-xl font-bold text-white mb-2">Password Reset Successful!</h2>
                        <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.5)' }}>
                            Your password has been updated successfully. You can now log in with your new password.
                        </p>
                        <button
                            onClick={() => navigate('/login')}
                            className="btn-primary w-full"
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Password reset form
    return (
        <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f0c1f 0%, #1a1533 50%, #0f0c1f 100%)' }}>
            <div className="glass-card rounded-2xl w-full max-w-md p-8 mx-4" style={{ background: 'rgba(14,14,26,0.95)' }}>
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 mx-auto"
                        style={{ background: 'rgba(155,125,240,0.15)' }}>
                        <Lock className="h-8 w-8" style={{ color: '#cbb9ff' }} />
                    </div>
                    <h2 className="text-xl font-bold text-white">Reset Your Password</h2>
                    {userName && (
                        <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
                            Hello, {userName}
                        </p>
                    )}
                    {userEmail && (
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                            {userEmail}
                        </p>
                    )}
                </div>

                {/* Error message */}
                {error && (
                    <div className="mb-4 p-3 rounded-lg text-sm"
                        style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)' }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                            New Password
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'rgba(155,125,240,0.5)' }} />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input pl-10 pr-10"
                                placeholder="••••••••"
                                minLength={6}
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                style={{ color: 'rgba(255,255,255,0.3)' }}
                            >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                            Confirm New Password
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'rgba(155,125,240,0.5)' }} />
                            <input
                                type={showConfirm ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="input pl-10 pr-10"
                                placeholder="••••••••"
                                minLength={6}
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirm(!showConfirm)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                style={{ color: 'rgba(255,255,255,0.3)' }}
                            >
                                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                        Password must be at least 6 characters
                    </p>

                    <button
                        type="submit"
                        disabled={loading || !password || !confirmPassword}
                        className="btn-primary w-full"
                    >
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>

                <div className="mt-4 text-center">
                    <Link
                        to="/login"
                        className="text-sm transition-colors"
                        style={{ color: 'rgba(203,185,255,0.7)' }}
                        onMouseOver={(e) => e.target.style.color = '#cbb9ff'}
                        onMouseOut={(e) => e.target.style.color = 'rgba(203,185,255,0.7)'}
                    >
                        <Building2 className="h-3 w-3 inline mr-1" />
                        Back to Login
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;